import apiRequest from 'helpers/api';
import { bypassUnsavedChangesWarning } from 'hooks/use-before-unload';

const ALREADY_SUBMITTED_THANKS_PATH = '/thanks?already_submitted=true';

let guardContext = { navigate: null, token: null, admin: false, allowAfterSubmission: false };

export const setGuardContext = (navigate, token, admin, allowAfterSubmission = false) => {
  guardContext = {
    navigate,
    token,
    admin: guardContext.admin || admin,
    allowAfterSubmission,
  };
};

export const isAdminSession = () => guardContext.admin;

export const markAsGuarded = thunkCreator => {
  const wrapped = (...args) => {
    const thunk = thunkCreator(...args);
    thunk.__candidateGuarded = true;
    return thunk;
  };
  return Object.assign(wrapped, thunkCreator);
};

const hasRejectedValues = check_values => {
  const rejected_values = check_values?.rejected_values;
  if (!rejected_values) return false;
  return (rejected_values.values?.length ?? 0) > 0 || (rejected_values.files?.length ?? 0) > 0;
};

export const isAlreadySubmittedApplication = application => {
  const { candidate, check_values, candidate_checks } = application ?? {};
  return (
    !!candidate?.submitted_at &&
    !candidate?.re_consent_requested_at &&
    !check_values?.rejected &&
    !hasRejectedValues(check_values) &&
    Array.isArray(candidate_checks) &&
    candidate_checks.length > 0 &&
    candidate_checks.every(check => check.submitted_at)
  );
};

export const redirectToAlreadySubmittedThanks = () => {
  bypassUnsavedChangesWarning();
  const { navigate } = guardContext;
  if (navigate) {
    navigate(ALREADY_SUBMITTED_THANKS_PATH);
    return;
  }
  window.location.replace(ALREADY_SUBMITTED_THANKS_PATH);
};

export const candidateSubmissionGuardMiddleware = store => next => action => {
  if (typeof action === 'function' && action.__candidateGuarded) {
    const { navigate, token, admin, allowAfterSubmission } = guardContext;

    if (!admin && !allowAfterSubmission && navigate && token) {
      const storeState = store.getState();
      const reduxCheckValues = storeState?.candidateApplication?.application?.check_values;
      if (reduxCheckValues?.rejected) {
        return next(action);
      }

      // `next(action)` returns RTK's special dispatched-thunk promise, which carries an
      // `.unwrap` method as an extra property on the promise object itself (not on its
      // resolved value). Returning it from inside a `.then()` lets JS auto-adopt its
      // resolved *value* into guardPromise, but that adoption drops the `.unwrap` property —
      // it belongs to the original promise object, not the value. Capturing the reference
      // directly in `dispatchedPromise` keeps `.unwrap` reachable after the guard check.
      let dispatchedPromise = null;
      const dispatch = () => {
        dispatchedPromise = next(action);
        return dispatchedPromise;
      };

      const guardPromise = apiRequest(`candidate_applications/${token}`, {}, 'get')
        .then(res => {
          const freshApp = res?.result ?? res ?? {};
          if (isAlreadySubmittedApplication(freshApp)) {
            redirectToAlreadySubmittedThanks();
            return new Promise(() => {});
          }
          return dispatch();
        })
        .catch(() => dispatch());

      guardPromise.unwrap = () =>
        guardPromise.then(() =>
          dispatchedPromise && typeof dispatchedPromise.unwrap === 'function'
            ? dispatchedPromise.unwrap()
            : guardPromise,
        );

      return guardPromise;
    }
  }
  return next(action);
};
